import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EsriFeatureLayer, EsriReactiveUtils} from '../../../config/esri.module';
import {ConfigService} from '../../../config/config.service';
import {MainMapService} from '../../../shared/service/main-map.service';
import {ArbeitsbereichService} from '../../../shared/service/arbeitsbereich.service';
import {Arbeitsbereich} from '../../../config/model/arbeitsbereich-config.model';
import {SearchSourceConfig} from '../../../config/model/search-config.model';
import LayerSearchSource from '@arcgis/core/widgets/Search/LayerSearchSource';
import {SERVICE_TYPES} from '../../../shared/enum/services-type.enum';
import {CustomSearchResolver} from './search-resolvers/custom-search-resolver';
import '@arcgis/map-components/components/arcgis-search';
import {ArcgisSearchElement} from './arcgis-search-element.interface';
import {takeUntil} from 'rxjs/operators';
import {TakeUntilDestroy} from '../../../core/component/take-until-destroy';

@Component({
  selector: 'search',
  templateUrl: './search.component.html',
  styleUrls: ['search.component.scss'],
  standalone: false,
})
export class SearchComponent extends TakeUntilDestroy implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('searchWidget') searchElementRef: ElementRef<ArcgisSearchElement>;
  @HostBinding('class.search-host-selector') cssSelectorForHostElement = true;

  private searchComponent: ArcgisSearchElement;
  private watchHandle: IHandle | null = null;

  constructor(
    private _configService: ConfigService,
    private _mainMapService: MainMapService,
    private _arbeitsbereichService: ArbeitsbereichService,
    private _translate: TranslateService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {}

  async ngAfterViewInit(): Promise<void> {
    this.createSearchComponent();
    this.updateSourceList();

    // listen on language change
    this._translate.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateSourceList();
    });

    // listen on arbeitsbereich change
    this._arbeitsbereichService.onChangeArbeitsbereichSubject.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateSourceList();
    });

    this.watchHandle = EsriReactiveUtils.watch(
      () => this.searchComponent.state,
      stateComponent => {
        if (stateComponent === 'ready') {
          this.updateSearchButtonTitle();
        }
      }
    ) as IHandle;
  }

  updateSearchButtonTitle() {
    const searchButton = document.querySelector('.esri-search__submit-button') as HTMLElement;
    if (searchButton) {
      searchButton.setAttribute('title', this._translate.instant('WIDGET.SEARCH.TITLE'));
    }
    const searchAlleOption = Array.from(document.querySelectorAll('li')).find(
      el => el.textContent?.trim() === 'Alle'
    ) as HTMLElement;

    if (searchAlleOption) {
      searchAlleOption.textContent = this._translate.instant('WIDGET.SEARCH.ALLE');
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.watchHandle.remove();
  }

  private createSearchComponent(): void {
    this.searchComponent = this.searchElementRef.nativeElement;
    this.searchComponent.view = this._mainMapService.mapView;
    this.searchComponent.popupEnabled = false;
    this.searchComponent.includeDefaultSources = false;
    this.searchComponent.searchDisabledAll = false;
    this.searchComponent.popupDisabled = true;
  }

  /**
   * @name updateSourceList
   * @description creates a source list with the global- and selected
   * arbeitsbereich-sources and updates the search-widget with it
   */
  private updateSourceList() {
    this.searchComponent.sources = [];

    // get abrbeitsbereich sources
    const arbeitsbereich: Arbeitsbereich = this._arbeitsbereichService.selectedArbeitsbereich;
    const idArbeitsbereich = arbeitsbereich ? arbeitsbereich.id : undefined;

    // get sources
    this.searchComponent.sources = this.buildSourceList(idArbeitsbereich);

    // set all placeholder
    const allPlaceholderKey = this.getAllPlaceholder(idArbeitsbereich);
    this._translate.get(allPlaceholderKey).subscribe(translate => {
      this.searchComponent.allPlaceholder = translate;
    });

    // set all sources as active
    this.searchComponent.activeSourceIndex = -1;
  }

  /**
   * @name buildSourceList
   * @param idArbeitsbereich
   * @description creates the source list from a search-config
   */
  private buildSourceList(idArbeitsbereich: string): (__esri.LayerSearchSource | __esri.LocatorSearchSource)[] {
    const sourceList: (__esri.LayerSearchSource | __esri.LocatorSearchSource)[] = [];

    this._configService.searchConfig.sources.forEach((searchSourceConfig: SearchSourceConfig) => {
      if (searchSourceConfig.fkArbeitsbereich === idArbeitsbereich || !searchSourceConfig.fkArbeitsbereich) {
        const source: __esri.LayerSearchSource | __esri.LocatorSearchSource = this.searchSourceFactory(
          searchSourceConfig
        );
        this.translateSource(source);
        sourceList.push(source);
      }
    });

    return sourceList;
  }

  /**
   * @name translateSource
   * @param source
   * @description sets the translated name and placehoder to the source
   */
  private translateSource(source: __esri.LayerSearchSource | __esri.LocatorSearchSource): void {
    this._translate.get([source.name, source.placeholder]).subscribe(translates => {
      source.name = translates[source.name];
      source.placeholder = translates[source.placeholder];
    });
  }

  /**
   * @name searchSourceFactory
   * @param searchSourceConfig
   * @description creates a search-source depending on the config-type (featureLayer or locator)
   */
  private searchSourceFactory(
    searchSourceConfig: SearchSourceConfig
  ): __esri.LayerSearchSource | __esri.LocatorSearchSource {
    switch (searchSourceConfig.type) {
      case SERVICE_TYPES.custom_search_Layer:
        const customLayerSource: __esri.LayerSearchSource = new LayerSearchSource(searchSourceConfig.properties);

        customLayerSource.layer = new EsriFeatureLayer({
          url: this._configService.getLayerUrl(searchSourceConfig.fkLayer),
        });

        const resolver = new CustomSearchResolver(searchSourceConfig.properties);
        customLayerSource.getSuggestions = params => {
          return resolver.getSuggestions(params);
        };
        return customLayerSource;

      case 'featureLayer':
        const url = this._configService.getLayerUrl(searchSourceConfig.fkLayer);
        const featureLayer = new EsriFeatureLayer({
          url: url,
          outFields: searchSourceConfig.properties.outFields,
        });

        const featureLayerSource: __esri.LayerSearchSource = { ...searchSourceConfig.properties };
        featureLayerSource.layer = featureLayer;
        return featureLayerSource;

      case 'locator':
        const locatorSource: __esri.LocatorSearchSource = {
          ...searchSourceConfig.properties,
        };
        locatorSource.url = this._configService.fullUrl(searchSourceConfig.properties.url);
        locatorSource.countryCode = searchSourceConfig.properties.countryCode;
        return locatorSource;

      default:
        throw Error(`searchSourceConfig from type ${searchSourceConfig.type} is not supported`);
    }
  }

  /**
   * @name getAllPlaceholder
   * @returns {string}
   * @description Sarches and returns the all-placeholder in the config.
   * @param idArbeitsbereich
   */
  private getAllPlaceholder(idArbeitsbereich: string): string {
    // get all-placehloder from arbeitsbereich
    let allPlaceholderConfigs = this._configService.searchConfig.allPlaceholders.filter(
      f => f.fkArbeitsbereich === idArbeitsbereich
    );
    if (allPlaceholderConfigs.length > 0) {
      return allPlaceholderConfigs[0].placeholder;
    }

    // get default all-placeholder
    allPlaceholderConfigs = this._configService.searchConfig.allPlaceholders.filter(
      f => f.fkArbeitsbereich === undefined
    );
    return allPlaceholderConfigs.length > 0 ? allPlaceholderConfigs[0].placeholder : '';
  }
}
